import "common/Charts/chartRegistry";
import React, { useState } from "react";
import { Chart } from "react-chartjs-2";
import type {
  ActiveElement,
  Chart as ChartInstance,
  ChartData,
  ChartEvent,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import dayjs from "dayjs";
import styled, { css, useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import {
  getNewLookTokens,
  NewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  FinanceCurrency,
  FinanceDirection,
  FinanceEntriesFilter,
  FinanceGranularity,
} from "features/finance/types";
import {
  FinancePeriod,
  formatCompactMoney,
  formatMoney,
  hasEntriesFilter,
} from "features/finance/utils";
import {
  GRANULARITY_TO_LABEL_FORMAT,
  GRANULARITY_TO_PERIOD_FORMAT,
} from "features/finance/constants";

enum ChartSeries {
  INCOME = "income",
  EXPENSES = "expenses",
  NET = "net",
}

interface FinancePeriodChartProps {
  periods: FinancePeriod[];
  granularity: FinanceGranularity;
  currency: FinanceCurrency;
  loading?: boolean;
  selectedPeriod?: string;
  filter: FinanceEntriesFilter;
  onPeriodClick: (period: string) => void;
}

type MixedChartData = ChartData<"bar" | "line", number[], string>;

const SERIES_LABEL = {
  [ChartSeries.INCOME]: "finance.chart.income",
  [ChartSeries.EXPENSES]: "finance.chart.expenses",
  [ChartSeries.NET]: "finance.chart.net",
} as const;

const getSeriesColor = (tokens: NewLookTokens, series: ChartSeries) =>
  ({
    [ChartSeries.INCOME]: tokens.up,
    [ChartSeries.EXPENSES]: tokens.down,
    [ChartSeries.NET]: tokens.accent,
  }[series]);

const getVisibleSeries = (filter: FinanceEntriesFilter) => {
  if (filter.direction === FinanceDirection.INCOME) return [ChartSeries.INCOME];
  if (filter.direction === FinanceDirection.EXPENSE)
    return [ChartSeries.EXPENSES];
  if (hasEntriesFilter(filter))
    return [ChartSeries.INCOME, ChartSeries.EXPENSES];
  return Object.values(ChartSeries);
};

const FinancePeriodChart = ({
  periods,
  granularity,
  currency,
  loading,
  selectedPeriod,
  filter,
  onPeriodClick,
}: FinancePeriodChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [hiddenSeries, setHiddenSeries] = useState<ChartSeries[]>([]);
  const visibleSeries = getVisibleSeries(filter);

  const isHidden = (series: ChartSeries) =>
    hiddenSeries.includes(series) || !visibleSeries.includes(series);

  const toggleSeries = (series: ChartSeries) =>
    setHiddenSeries((current) =>
      current.includes(series)
        ? current.filter((hidden) => hidden !== series)
        : [...current, series]
    );

  const barColors = (color: string, alpha: number) =>
    periods.map((period) =>
      hexToRGB(
        color,
        selectedPeriod && period.period !== selectedPeriod ? 0.25 : alpha
      )
    );

  const labelFormat = GRANULARITY_TO_LABEL_FORMAT[granularity];
  const data: MixedChartData = {
    labels: periods.map((period) =>
      dayjs(period.period, GRANULARITY_TO_PERIOD_FORMAT[granularity]).format(
        labelFormat
      )
    ),
    datasets: [
      {
        type: "line" as const,
        label: t(SERIES_LABEL[ChartSeries.NET]),
        data: periods.map((period) => period.net),
        hidden: isHidden(ChartSeries.NET),
        borderColor: tokens.accent,
        backgroundColor: tokens.accent,
        borderWidth: 2,
        pointRadius: granularity === FinanceGranularity.DAY ? 2 : 3,
        pointHoverRadius: 5,
        tension: 0.3,
        cubicInterpolationMode: "monotone",
        order: 0,
      },
      {
        type: "bar" as const,
        label: t(SERIES_LABEL[ChartSeries.INCOME]),
        data: periods.map((period) => period.income),
        hidden: isHidden(ChartSeries.INCOME),
        backgroundColor: barColors(tokens.up, 0.8),
        hoverBackgroundColor: tokens.up,
        borderRadius: 3,
        maxBarThickness: 22,
        order: 1,
      },
      {
        type: "bar" as const,
        label: t(SERIES_LABEL[ChartSeries.EXPENSES]),
        data: periods.map((period) => period.expenses),
        hidden: isHidden(ChartSeries.EXPENSES),
        backgroundColor: barColors(tokens.down, 0.75),
        hoverBackgroundColor: tokens.down,
        borderRadius: 3,
        maxBarThickness: 22,
        order: 2,
      },
    ],
  };

  const onClick = (
    event: ChartEvent,
    _: ActiveElement[],
    chart: ChartInstance
  ) => {
    if (!event.native) return;
    const [element] = chart.getElementsAtEventForMode(
      event.native,
      "index",
      { intersect: false },
      false
    );
    if (element) onPeriodClick(periods[element.index].period);
  };

  const onHover = (event: ChartEvent, elements: ActiveElement[]) => {
    const target = event.native?.target as HTMLElement | undefined;
    if (target) target.style.cursor = elements.length ? "pointer" : "default";
  };

  const font = { size: 11, family: theme.typography.fontFamily };
  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    onClick,
    onHover,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tokens.panel,
        titleColor: tokens.ink,
        bodyColor: tokens.secondary,
        footerColor: tokens.muted,
        borderColor: tokens.hairlineStrong,
        borderWidth: 1,
        padding: 10,
        boxPadding: 4,
        usePointStyle: true,
        titleFont: { ...font, weight: 600 },
        bodyFont: font,
        footerFont: { ...font, weight: 400 },
        callbacks: {
          label: (item: TooltipItem<"bar" | "line">) =>
            ` ${item.dataset.label}: ${formatMoney(
              Number(item.raw),
              currency
            )}`,
          footer: () =>
            granularity === FinanceGranularity.MONTH
              ? t("finance.chart.click_month")
              : t("finance.chart.click_day"),
        },
      },
    },
    scales: {
      x: {
        type: "category",
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: tokens.muted,
          font,
          maxRotation: 0,
          autoSkipPadding: 8,
        },
      },
      y: {
        type: "linear",
        beginAtZero: true,
        grid: { color: tokens.hairline, drawTicks: false },
        border: { display: false, dash: [3, 4] },
        ticks: {
          color: tokens.muted,
          font,
          padding: 8,
          maxTicksLimit: 5,
          callback: (value) => formatCompactMoney(Number(value), currency),
        },
      },
    },
  };

  return (
    <Wrapper>
      <Legend>
        {visibleSeries.map((series) => (
          <LegendItem
            key={series}
            type="button"
            aria-pressed={!hiddenSeries.includes(series)}
            $isHidden={hiddenSeries.includes(series)}
            onClick={() => toggleSeries(series)}
          >
            <Swatch
              $color={getSeriesColor(tokens, series)}
              $isLine={series === ChartSeries.NET}
            />
            {t(SERIES_LABEL[series])}
          </LegendItem>
        ))}
      </Legend>
      <ChartBox>
        <LoadingSpinnerOverlay
          isLoading={loading}
          overlayOpacity={0.7}
          backgroundColor={tokens.panel}
        >
          <Chart
            type="bar"
            data={data as ChartData<"bar", number[], string>}
            options={options as ChartOptions<"bar">}
          />
        </LoadingSpinnerOverlay>
      </ChartBox>
    </Wrapper>
  );
};

export default FinancePeriodChart;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  justify-content: flex-end;
  margin-right: -6px;
`;
const LegendItem = styled.button<{ $isHidden: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${(p) =>
    p.$isHidden &&
    css`
      opacity: 0.45;
      text-decoration: line-through;
    `}
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
const Swatch = styled.span<{ $color: string; $isLine?: boolean }>`
  width: ${(p) => (p.$isLine ? 14 : 8)}px;
  height: ${(p) => (p.$isLine ? 2 : 8)}px;
  border-radius: 2px;
  background: ${(p) => p.$color};
`;
const ChartBox = styled.div`
  position: relative;
  height: 300px;
  @media (max-width: 600px) {
    height: 240px;
  }
`;
